import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

interface PricingReasonModalProps {
  open: boolean;
  reason: string;
  isSaving: boolean;
  priceFinalizedEmailRequested: boolean;
  onPriceFinalizedEmailRequestedChange: (requested: boolean) => void;
  showPriceFinalizedEmail?: boolean;
  priceFinalizedPreview: {
    recipients: {
      to: string[];
      cc: string[];
      loading: boolean;
    };
    rows: Array<{
      label: string;
      beforeValue: string;
      beforeAmountValue?: string;
      afterValue: string;
      afterAmountValue?: string;
      note?: string;
      changed: boolean;
    }>;
  };
  onOpenChange: (open: boolean) => void;
  onReasonChange: (reason: string) => void;
  onSubmit: (reason: string) => void;
}

export function PricingReasonModal({
  open,
  reason,
  isSaving,
  priceFinalizedEmailRequested,
  onPriceFinalizedEmailRequestedChange,
  showPriceFinalizedEmail = true,
  priceFinalizedPreview,
  onOpenChange,
  onReasonChange,
  onSubmit
}: PricingReasonModalProps) {
  const trimmed = reason.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-pricing-v2
        className="prm-root w-[calc(100vw-32px)] sm:max-w-[760px] max-h-[calc(100vh-32px)] p-0 gap-0 overflow-hidden"
      >
        <form
          className="prm-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed === '' || isSaving) {
              return;
            }
            onSubmit(trimmed);
          }}
        >
          <div className="prm-hdr">
            <DialogTitle className="prm-title">Pricing change reason</DialogTitle>
            <DialogDescription className="prm-desc">
              This loan is locked. Add a reason before saving pricing changes.
            </DialogDescription>
          </div>

          <div className="prm-body">
            <label className="prm-label" htmlFor="pricing-change-reason">
              Reason
            </label>
            <textarea
              id="pricing-change-reason"
              className="prm-textarea"
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              autoFocus
              rows={4}
              disabled={isSaving}
            />

            {showPriceFinalizedEmail && (
              <div className="prm-finalized">
                <div className="prm-finalized-hdr">
                  <div>
                    <div className="prm-label">Price Finalized email</div>
                    <p>Send funding a finalized pricing package after Encompass readback.</p>
                  </div>
                  <div className="prm-segment" role="group" aria-label="Send Price Finalized email">
                    <button
                      type="button"
                      className={priceFinalizedEmailRequested ? 'active' : ''}
                      onClick={() => onPriceFinalizedEmailRequestedChange(true)}
                      disabled={isSaving}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={!priceFinalizedEmailRequested ? 'active' : ''}
                      onClick={() => onPriceFinalizedEmailRequestedChange(false)}
                      disabled={isSaving}
                    >
                      No
                    </button>
                  </div>
                </div>

                {priceFinalizedEmailRequested && (
                  <div className="prm-preview" aria-label="Price Finalized email preview">
                    <div className="prm-preview-top">
                      <span>Preview</span>
                      <em>Final email sends after pricing is confirmed.</em>
                    </div>
                    <div className="prm-preview-recipients">
                      <div>
                        <strong>To</strong>
                        <span>
                          {priceFinalizedPreview.recipients.loading
                            ? 'Loading funding recipients...'
                            : priceFinalizedPreview.recipients.to.join(', ') || 'Configured funding recipients'}
                        </span>
                      </div>
                      <div>
                        <strong>CC</strong>
                        <span>{priceFinalizedPreview.recipients.cc.join(', ') || 'Saving user'}</span>
                      </div>
                    </div>
                    <div className="prm-preview-table">
                      <div className="prm-preview-head">
                        <span>Value</span>
                        <strong>Before</strong>
                        <strong>Before $</strong>
                        <strong>After</strong>
                        <strong>After $</strong>
                      </div>
                      {priceFinalizedPreview.rows.map((row) => (
                        <div key={row.label} className={row.changed ? 'prm-preview-row changed' : 'prm-preview-row'}>
                          <span>{row.label}</span>
                          <em className="prm-before">{row.beforeValue || '-'}</em>
                          <em className="prm-before">{row.beforeAmountValue || '-'}</em>
                          <strong className="prm-after">{row.afterValue || '-'}</strong>
                          <strong className="prm-after">{row.afterAmountValue || '-'}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="prm-footer">
            <button
              type="button"
              className="prm-btn secondary"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="prm-btn primary"
              disabled={trimmed === '' || isSaving}
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
